import {
  createStyles,
  Grid,
  Icon,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import * as classNames from 'classnames';
import Tab from 'components/lib/navigation/Tab';
import Tabs from 'components/lib/navigation/Tabs';
import { IStatsList, PeriodValue, StatsResolution, ViewType } from 'components/lib/stats/constants';
import RecentReadsCovers from 'components/lib/stats/RecentReadsCovers';
import StatsGraphArea from 'components/lib/stats/StatsGraphArea';
import Time from 'components/lib/stats/Time';
import { Duration } from 'luxon';
import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { ICheckout, IProfileState } from '../profile/model';
import { IStatsState } from '../reading-stats/model';
import {
  getRecentReadIDs,
  loadMinutesReadTodayAndForever,
  loadStats,
} from '../reading-stats/services';

const styles = (theme: Theme) =>
  createStyles({
    greyText: {
      color: theme.palette.grey[600],
    },
    header: {
      backgroundColor: theme.palette.grey[200],
      boxShadow: `inset 0 20px 8px -20px rgba(0, 0, 0, 0.2)`,
      color: theme.palette.secondary[500],
      height: `200px`,
      position: 'relative',
    },
    headerBtm: {
      backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000)',
      bottom: 0,
      height: 4,
      left: 0,
      opacity: 0.2,
      position: 'absolute',
      width: '100%',
      zIndex: 10,
    },
    headerContent: {
      bottom: 0,
      height: `100%`,
      left: 0,
      paddingBottom: 50,
      position: 'absolute',
      width: `100%`,
      zIndex: 20,
    },
    headerOverlay: {
      backgroundColor: '#FFFFFF',
      bottom: 0,
      height: `100%`,
      left: 0,
      opacity: 0.6,
      position: 'absolute',
      width: `100%`,
      zIndex: 8,
    },
    tabIcon: {
      '@media (max-width: 600px)': {
        fontSize: `20px`,
        height: 24,
        marginRight: `6px`,
      },
      marginRight: `16px`,
    },
    tabText: {
      '@media (max-width: 600px)': {
        fontSize: `18px`,
      },
    },
    tabsContainer: {
      position: 'relative',
      zIndex: 10,
    },
    totalSumContainer: {
      backgroundColor: theme.palette.grey[800],
      color: theme.palette.grey[200],
      minHeight: 160,
      padding: `20px 0 40px 0`,
    },
  });

interface IStoreFunction {
  getRecentReadIDs: () => void;
  loadMinutesReadTodayAndForever: () => void;
  loadStats: (reso: StatsResolution) => void;
}
interface IStateProps {
  checkouts: ICheckout[] | null;
  stats: IStatsState;
}

interface IState {
  tabView: ViewType;
  recentISBNs: string[];
  period: PeriodValue;
}

export interface IProps extends WithStyles<typeof styles> {
  drawerOpen: boolean;
  onClose: () => any;
  profile: IProfileState;
}
type allProps = IProps & IStoreFunction & IStateProps & InjectedTranslateProps;

class MyReading extends React.Component<allProps> {
  public state: IState;

  constructor(props: allProps) {
    super(props);
    this.state = {
      period: PeriodValue.week,
      recentISBNs: [],
      tabView: ViewType.time,
    };
  }

  public render() {
    const { classes, stats, t } = this.props;
    const { tabView, recentISBNs, period } = this.state;
    const statsData: IStatsList =
      period === PeriodValue.year ? stats.weeks : stats.days;

    const minsToday: number | null = stats.today
      ? Math.round(stats.today.seconds_read / 60)
      : null;
    const totalReadDur = stats.all_time
      ? Duration.fromMillis(stats.all_time.seconds_read * 1000).shiftTo(
          'hours',
          'minutes'
        )
      : null;

    const reso = this.getResulotionFromPeriod(this.state.period);
    const isBusy = stats.data_load_state[`${reso}_resolution_busy`];
    const isDataLoaded = stats.data_load_state[`${reso}_resolution_loaded`];

    return (
      <div>
        <Grid
          container={true}
          direction="column"
          justify="center"
          alignItems="center"
          className={classes.header}
        >
          <Grid
            container={true}
            direction="column"
            justify="center"
            alignItems="center"
            className={classes.headerContent}
          >
            <Typography variant="headline" color="inherit">
              {minsToday ? (
                <React.Fragment>
                  {t('web_message_read_minutes_today', {count: minsToday})}
                </React.Fragment>
              ) : (
                <React.Fragment>{t('web_message_not_read_today')}</React.Fragment>
              )}
            </Typography>
            <Typography variant="subheading" className={classes.greyText}>
            {t('web_your_total_reading_stats')}
            </Typography>
          </Grid>

          <RecentReadsCovers isbns={recentISBNs} />

          <div className={classes.headerOverlay} />
          <div className={classes.headerBtm} />
        </Grid>

        <div className={classes.tabsContainer}>
          <Tabs value={tabView} onChange={this.onTabClick} variant="raised">
            <Tab value={ViewType.time} color="green">
              <Grid
                container={true}
                direction="row"
                justify="center"
                alignItems="center"
              >
                <Icon
                  className={classNames(classes.tabIcon, {
                    'icon-read-time': true,
                  })}
                />
                <Typography
                  variant="headline"
                  color="inherit"
                  className={classes.tabText}
                >
                  {t('web_time_read')}
                </Typography>
              </Grid>
            </Tab>
            <Tab value={ViewType.speed} color="blue">
              <Grid
                container={true}
                direction="row"
                justify="center"
                alignItems="center"
              >
                <Icon
                  className={classNames(classes.tabIcon, {
                    'icon-read-speed': true,
                  })}
                />
                <Typography
                  variant="headline"
                  color="inherit"
                  className={classes.tabText}
                >
                  {t('web_reading_speed')}
                </Typography>
              </Grid>
            </Tab>
          </Tabs>
        </div>

        <StatsGraphArea
          viewType={tabView}
          onPeriodFilterChange={this.handlePeriodFilterChange}
          period={period}
          data={statsData}
          isBusy={isBusy}
          isDataLoaded={isDataLoaded}
        />

        <Grid
          container={true}
          direction="column"
          alignItems="center"
          justify="space-between"
          className={classes.totalSumContainer}
        >
          <Typography
            variant="caption"
            color="inherit"
            style={{ color: '#FFFFFF', opacity: 0.6 }}
          >
            {this.getTimeSinceStart()}
          </Typography>

          <Grid item={true}>
            {totalReadDur ? (
              <Time
                hours={Math.round(totalReadDur.hours)}
                minutes={Math.round(totalReadDur.minutes)}
              />
            ) : (
              <Time />
            )}
          </Grid>
        </Grid>
      </div>
    );
  }

  public componentDidMount() {
    if (this.props.checkouts !== null) {
      const recentISBNs = this.props.getRecentReadIDs();
      if (recentISBNs) {
        this.setState({
          recentISBNs,
        });
      }
    }

    this.props.loadMinutesReadTodayAndForever();
    this.props.loadStats(this.getResulotionFromPeriod(this.state.period));
  }

  public componentDidUpdate() {
    if (
      this.state.recentISBNs.length === 0 &&
      this.props.checkouts !== null
    ) {
      const recentISBNs = this.props.getRecentReadIDs();
      if (recentISBNs) {
        this.setState({
          recentISBNs,
        });
      }
    }
  }

  private getResulotionFromPeriod(period: PeriodValue): StatsResolution {
    if (period === PeriodValue.week || period === PeriodValue.month) {
      return StatsResolution.day;
    }
    return StatsResolution.week;
  }

  private handlePeriodFilterChange = (value: PeriodValue) => {
    this.setState(
      {
        period: value,
      },
      () => {
        this.props.loadStats(this.getResulotionFromPeriod(this.state.period));
      }
    );
  };

  private getTimeSinceStart = () => {
    const { stats, t } = this.props;
    if (stats && stats.stats_duration) {
      if (stats.stats_duration.total_days > 365) {
        return t('web_total_in_years_and_days', {
          days: stats.stats_duration.days,
          years: stats.stats_duration.years,
        });
      }
      return t('web_total_in_days', {count: stats.stats_duration.total_days});
    }
    return t('cms_total');
  };

  private onTabClick = (val: ViewType) => {
    this.setState({
      tabView: val,
    });
  };
}
const mapStateToProps = (state: {
  profile: IProfileState;
  stats: IStatsState;
}): any => ({
  checkouts: state.profile.checkouts,
  stats: state.stats,
});

const enhance = compose(
  translate(),
  connect(
    mapStateToProps,
    { getRecentReadIDs, loadMinutesReadTodayAndForever, loadStats }
  ),
  withStyles(styles),
);

export default enhance(MyReading);



// WEBPACK FOOTER //
// ./src/routes/MyReading.tsx